import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ComboboxPopover, useComboboxState } from 'ariakit'
import { csx, Flex } from '@vtex/admin-ui'
import { useAutoAnimate } from '@formkit/auto-animate/react'

import {
  numericSearchIds,
  useSearchOptionsByValue,
} from './hooks/useSearchOptionsByValue'
import { ComboboxSearch, ComboboxSearchItem } from './components'
import type { useProductSearch } from './hooks'

import * as styles from './ProductSearch.styles'

const ESCAPE_KEY = 'Escape'
const ENTER_KEY = 'Enter'
const ARROW_DOWN_KEY = 'ArrowDown'
const ARROW_UP_KEY = 'ArrowUp'

interface ProductSearchProps {
  state: ReturnType<typeof useProductSearch>
  loading?: boolean
}

export function ProductSearch({
  state: {
    selectedItem,
    setSelectedItem,
    pinnedSearchBy,
    togglePinnedSearchBy,
  },
  loading = false,
}: ProductSearchProps) {
  const { formatMessage } = useIntl()
  const [value, setValue] = useState(selectedItem.value)
  const [searchByOnFocus, setSearchByOnFocus] =
    useState<typeof selectedItem.id>(undefined)
  const [parent, enable] = useAutoAnimate()

  const searchOptions = useSearchOptionsByValue({
    value,
    pinnedSearchBy,
  })

  const combobox = useComboboxState({
    gutter: 8,
    value,
    setValue,
  })

  const handleOnClearSearch = () => {
    combobox.setValue('')
    setSelectedItem({ value: '' })
  }

  const handleOnSubmit = (value: string) => {
    setSelectedItem({
      id: searchByOnFocus || selectedItem?.id || searchOptions[0],
      value,
    })
    combobox.hide()
  }

  const handleResetSearch = () => {
    setSearchByOnFocus(undefined)
    combobox.setValue(selectedItem.value)
  }

  useEffect(() => {
    enable(false)
  }, [enable])

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const isSearchingById =
    numericSearchIds.includes(searchByOnFocus as any) ||
    numericSearchIds.includes(selectedItem.id as any)
  /* eslint-enable  @typescript-eslint/no-explicit-any */

  return (
    <div className={csx(styles.wrapper)}>
      <ComboboxSearch
        state={combobox}
        loading={loading}
        onClear={handleOnClearSearch}
        placeholder={formatMessage({
          id: 'admin/admin.catalog.products-search-placeholder',
          defaultMessage: 'Use / to search',
        })}
        onSubmit={handleOnSubmit}
        disabled={loading}
        searchingBy={searchByOnFocus ?? selectedItem?.id}
        onBlur={handleResetSearch}
        maxLength={isSearchingById ? 10 : undefined}
        onKeyDown={(evt) => {
          if (evt.key === ESCAPE_KEY) {
            handleResetSearch()
          }

          if (evt.key === ENTER_KEY) {
            return handleOnSubmit(combobox.value)
          }

          if ([ARROW_DOWN_KEY, ARROW_UP_KEY].includes(evt.key)) {
            const activeItemIndex = searchOptions.findIndex(
              (option) =>
                (searchByOnFocus || selectedItem.id || searchOptions[0]) ===
                option
            )

            if (evt.key === ARROW_DOWN_KEY) {
              const nextItemIndex = activeItemIndex + 1

              return setSearchByOnFocus(
                searchOptions[
                  nextItemIndex > searchOptions.length - 1 ? 0 : nextItemIndex
                ]
              )
            }

            setSearchByOnFocus(searchOptions.at(activeItemIndex - 1))
          }
        }}
        onChange={(evt) => combobox.setValue(evt.target.value)}
      />
      <ComboboxPopover
        state={combobox}
        className={styles.comboboxPopover}
        wrapperProps={{
          className: csx(styles.comboboxPopoverWrapper),
        }}
      >
        <Flex direction="column" ref={parent}>
          {searchOptions.map((option) => (
            <ComboboxSearchItem
              key={option}
              id={option}
              isSelected={
                searchByOnFocus
                  ? searchByOnFocus === option
                  : (selectedItem.id || searchOptions[0]) === option
              }
              isPinned={pinnedSearchBy === option}
              value={combobox.value}
              onClick={() => {
                setSelectedItem({ id: option, value: combobox.value })
                setSearchByOnFocus(undefined)
              }}
              onTogglePin={(evt) => {
                evt.stopPropagation()
                enable(true)
                togglePinnedSearchBy(option)
                setTimeout(() => enable(false), 300)
              }}
              focusable={false}
              clickOnEnter={false}
            />
          ))}
        </Flex>
      </ComboboxPopover>
    </div>
  )
}
